import { get } from 'lodash';
import i18next from 'i18next';

import { mapColumnIdsAndNormalizedColumnIds, normalizeJsonObject } from '@backoffice/utils';
import ImportExcelFileModal from '@backoffice/components/ImportExcelFileModal';

import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_IMPORT_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { IMPORT_MODAL_STEPS, getValidationFileConfig } from '@commons/Modals/MultiStepModal';
import importTypeXlsConstants from '@commons/Excels/constants/importType.xslx.constant';
import storeSupplierProfileMappingXlsConstants from '@commons/Excels/constants/storeSupplierProfileMapping.xlsx.constant';

export const formatStoreSupplierProfileMappingBatchUpdateFile = (
  storeSupplierProfileMappingWorkbook,
) => {
  const normalizedStoreSupplierProfileMappingWorkbook = normalizeJsonObject(
    storeSupplierProfileMappingWorkbook,
  );

  const sheetConfiguration = storeSupplierProfileMappingXlsConstants.getSheetConfiguration();

  const columnIds = storeSupplierProfileMappingXlsConstants.getNormalizedColumnIds();

  return Object.keys(normalizedStoreSupplierProfileMappingWorkbook).reduce((acc, sheetName) => {
    if (!acc[importTypeXlsConstants.KEY]) {
      acc[importTypeXlsConstants.KEY] = get(
        normalizedStoreSupplierProfileMappingWorkbook,
        'ImportType[0].importtype',
        null,
      );
    }

    if (sheetName === sheetConfiguration.STORE_SUPPLIER_PROFILE_MAPPING.name) {
      acc[sheetConfiguration.STORE_SUPPLIER_PROFILE_MAPPING.id] =
        normalizedStoreSupplierProfileMappingWorkbook[sheetName]
          // Ignore added lines
          .filter(({ id }) => id)
          .map((row) => mapColumnIdsAndNormalizedColumnIds(row, columnIds));
    }

    return acc;
  }, {});
};

export const getImportStoreSupplierProfileMappingModalConfig = (step, props) => {
  switch (step) {
    case IMPORT_MODAL_STEPS.SELECT_FILE:
      return getSelectFileConfig(props);
    case IMPORT_MODAL_STEPS.VALIDATE_FILE:
      return getValidationFileConfig({
        ...props,
        title: i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_IMPORT_TEMPLATE_EDIT_SUPPLIER_ITEMS_TITLE'),
        subTitle: i18next.t(
          'ADMIN.SUPPLIER_PRODUCTS.MODAL_IMPORT_TEMPLATE_EDIT_SUPPLIER_ITEMS_TITLE',
        ),
      });
    case IMPORT_MODAL_STEPS.VALIDATED_FILE:
      return getValidatedFileConfig(props);
    case IMPORT_MODAL_STEPS.ERROR_FILE:
      return getErrorFileConfig(props);
    default:
      return {
        width: '542px',
        height: 'auto',
        component: ImportExcelFileModal,
      };
  }
};

export const getSelectFileConfig = ({ selectedFile, handleFileChange }) => ({
  width: '542px',
  height: 'auto',
  type: 'action',
  title: i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_IMPORT_TEMPLATE_EDIT_SUPPLIER_ITEMS_TITLE'),
  icon: '/images/inpulse/file-upload-black-small.svg',
  data: {
    subTitle: i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_IMPORT_TEMPLATE_EDIT_SUPPLIER_ITEMS_TITLE'),
    selectedFile,
    setSelectedFile: (file) => handleFileChange(file),
  },
  component: ImportExcelFileModal,
  actions: [
    {
      ...GENERIC_MODAL_CANCEL_BUTTON(),
      handleClick: () => handleFileChange(null),
    },
    {
      ...GENERIC_MODAL_IMPORT_BUTTON(),
      isDisabled: true,
      handleClick: () => false,
    },
  ],
});

const getValidatedFileConfig = ({ updatedFile = null, handleFileImport }) => ({
  width: '542px',
  height: 'auto',
  type: 'success',
  title: i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_IMPORT_TEMPLATE_EDIT_SUPPLIER_ITEMS_TITLE'),
  icon: '/images/inpulse/check-white-small.svg',
  component: ImportExcelFileModal,
  data: {
    validatedFile: true,
    selectedFile: updatedFile,
  },
  actions: [
    {
      ...GENERIC_MODAL_IMPORT_BUTTON(),
      handleClick: () => handleFileImport(),
    },
  ],
});

export const getErrorFileConfig = ({
  updatedFile,
  handleFileChange,
  exportErrors,
  statusCode,
  storeName,
}) => {
  const data = {
    subTitle: i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_IMPORT_TEMPLATE_SELECT_FILE_ERROR_CONTENT'),
    additionalText: i18next.t(
      'ADMIN.SUPPLIERS.IMPORT_TEMPLATE_BATCH_SELECT_FILE_ERROR_ADDITIONAL_TEXT',
      {
        storeName,
      },
    ),
    validatedFile: false,
    selectedFile: updatedFile,
    ...(statusCode !== 200 && {
      exportXLS: exportErrors,
    }),
  };

  return {
    width: '542px',
    height: 'auto',
    type: 'error',
    title: i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_IMPORT_TEMPLATE_BATCH_SELECT_FILE_ERROR_TITLE'),
    icon: '/images/inpulse/info-white-small.svg',
    data,
    component: ImportExcelFileModal,
    actions: [
      {
        key: 0,
        color: 'inpulse-default',
        label: i18next.t('GENERAL.CLOSE'),
        icon: '/images/inpulse/close-white-small.svg',
        handleClick: () => {
          handleFileChange(null);
        },
      },
    ],
  };
};
